import { showHttpError } from '@calendly/alert'
import { parseQuery } from '@calendly/http-tools'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { pushQueryParams, setGetParams } from 'actions/core/navigation'
import { Segment } from 'core/analytics/segment'

import { getEntries, getAggregations, createExport, createOutgoingCommsExport } from './api'
import {
  PAGE_SIZES,
  STATUS_FAILED,
  STATUS_INITIAL,
  STATUS_LOADED,
  STATUS_LOADING,
  CONFIRMATION_POPUP_TYPE,
  NOT_ALLOWED_POPUP_TYPE,
  resetFiltersObj
} from './constants'
import {
  pageTokenSelector,
  sortFieldSelector,
  pageSizeSelector,
  sortOrderSelector,
  activityLogPaginationSelector,
  pageNumberSelector,
  filtersSelector,
  activityLogCountSelector,
  searchTermSelector
} from './selectors'

const downloadExport = (path) => {
  window.open(path, '_self')
}

const commaSeparatedFilters = (filters, filterKey) => filters[filterKey] && filters[filterKey].join(',')

export const requestActivityLogExport = createAsyncThunk(
  'activityLog/exports/request',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState()
    const sortField = sortFieldSelector(state)
    const sortOrder = sortOrderSelector(state)
    const filters = filtersSelector(state)
    const searchTerm = searchTermSelector(state)

    const params = {
      action: commaSeparatedFilters(filters, 'activity'),
      actor: commaSeparatedFilters(filters, 'by_whom'),
      max_occurred_at: filters.date_range?.endDate,
      min_occurred_at: filters.date_range?.startDate,
      namespace: commaSeparatedFilters(filters, 'category'),
      search_term: searchTerm,
      sort: `${sortField}:${sortOrder}`,
    }

    Object.keys(params).forEach((key) => {
      if (!params[key]?.length) {
        delete params[key]
      }
    })

    const [success, response] = await createExport(params)

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue()
    }

    switch (response.location) {
      case 'email':
        return {
          exportPopupToRender: CONFIRMATION_POPUP_TYPE
        }
      case 'inline':
      default:
        downloadExport(response.url)
        return {
          exportPopupToRender: null
        }
    }
  }
)

export const requestOutgoingCommsExport = createAsyncThunk(
  'activityLog/exports/requestOutgoingComms',
  async (_, { dispatch, rejectWithValue }) => {
    const [success, response] = await createOutgoingCommsExport()

    if (!success) {
      if (response.type === 'policy') {
        return rejectWithValue({ exportPopupToRender: NOT_ALLOWED_POPUP_TYPE })
      }
      dispatch(showHttpError(response))
      return rejectWithValue({ exportPopupToRender: null })
    }

    return null
  }
)

const fetchActivityLogAggregations = createAsyncThunk(
  'activityLog/aggregations/fetch',
  async (_, { dispatch, rejectWithValue }) => {
    const [aggregationsSuccess, aggregationsResponse] = await getAggregations()

    if (!aggregationsSuccess) {
      dispatch(showHttpError(aggregationsResponse))
      return rejectWithValue(null)
    }

    return aggregationsResponse
  }
)

const fetchActivityLogEntries = createAsyncThunk(
  'activityLog/entries/fetch',
  async (options, { getState, dispatch, rejectWithValue }) => {
    const state = getState()

    const sortField = sortFieldSelector(state)
    const sortOrder = sortOrderSelector(state)
    const pageSize = pageSizeSelector(state)
    const pagination = activityLogPaginationSelector(state)
    const pageNumber = pageNumberSelector(state)
    const filters = filtersSelector(state)
    const searchTerm = searchTermSelector(state)

    let pageToken

    if (pageNumber !== 0) {
      pageToken = pageTokenSelector(state)
      if (typeof options?.next === 'boolean') {
        pageToken = options.next ? pagination.next_page_token : pagination.previous_page_token
      }
    }

    const queryParams = {
      action: commaSeparatedFilters(filters, 'activity'),
      actor: commaSeparatedFilters(filters, 'by_whom'),
      count: pageSize,
      max_occurred_at: filters.date_range?.endDate,
      min_occurred_at: filters.date_range?.startDate,
      namespace: commaSeparatedFilters(filters, 'category'),
      page_token: pageToken ?? undefined,
      search_term: searchTerm || undefined,
      sort: `${sortField}:${sortOrder}`,
    }

    const [entriesSuccess, entriesResponse] = await getEntries(queryParams)

    if (!entriesSuccess) {
      dispatch(showHttpError(entriesResponse))
      return rejectWithValue(null)
    }

    return entriesResponse
  }
)

const initialState = {
  aggregations: {},
  collection: [],
  exceedsMaxTotalCount: false,
  exportPopupToRender: null,
  filters: {},
  pageNumber: 0,
  pageSize: PAGE_SIZES[0],
  pageToken: undefined,
  pagination: {},
  searchTerm: undefined,
  selectedPeriod: undefined,
  showFilterBar: false,
  sortField: 'occurred_at',
  sortOrder: 'desc',
  status: STATUS_INITIAL,
  totalCount: 0,
}

const resetPageParams = {
  page: initialState.pageNumber,
  page_token: initialState.pageToken
}

const slice = createSlice({
  name: 'activityLog',
  initialState,
  reducers: {
    clearExportResults: (state) => {
      state.exportPopupToRender = null
    },
    reset: () => initialState,
    setSorting: (state, { payload }) => {
      if (state.sortField === payload) {
        state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        state.sortOrder = 'asc'
      }

      state.sortField = payload
    },
    setSortField: (state, { payload }) => {
      state.sortField = payload
    },
    setSortOrder: (state, { payload }) => {
      state.sortOrder = payload
    },
    setPageSize: (state, { payload }) => {
      state.pageSize = payload
    },
    setPageNumber: (state, { payload }) => {
      state.pageNumber = payload
    },
    setPageToken: (state, { payload }) => {
      state.pageToken = payload
    },
    /**
     * 'payload.values' should either be an object with keys (date filter) or an array of values (other filters)
     */
    setFilter: (state, { payload }) => {
      const filterValues = Object.keys(payload.values).length > 0 ? payload.values : undefined

      state.filters = {
        ...state.filters,
        [payload.name]: filterValues
      }
    },
    setSearchTerm: (state, { payload }) => {
      state.searchTerm = payload
    },
    setShowFilterBar: (state, { payload }) => {
      state.showFilterBar = payload
    },
    setSelectedPeriod: (state, { payload }) => {
      state.selectedPeriod = payload
    },
    resetFilters: (state) => {
      state.filters = initialState.filters
      state.pagination = initialState.pagination
      state.pageNumber = initialState.pageNumber
      state.selectedPeriod = initialState.selectedPeriod
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchActivityLogAggregations.pending, (state) => {
      state.status = STATUS_LOADING
    })
    builder.addCase(fetchActivityLogAggregations.fulfilled, (state, action) => {
      state.status = STATUS_LOADED
      state.aggregations = action.payload
    })
    builder.addCase(fetchActivityLogAggregations.rejected, (state) => {
      state.status = STATUS_FAILED
    })
    builder.addCase(fetchActivityLogEntries.pending, (state) => {
      state.status = STATUS_LOADING
    })
    builder.addCase(fetchActivityLogEntries.fulfilled, (state, action) => {
      state.status = STATUS_LOADED
      state.collection = action.payload.collection
      state.pagination = action.payload.pagination
      state.totalCount = action.payload.total_count
      state.exceedsMaxTotalCount = action.payload.exceeds_max_total_count
    })
    builder.addCase(fetchActivityLogEntries.rejected, (state) => {
      state.status = STATUS_FAILED
    })
    builder.addCase(requestActivityLogExport.fulfilled, (state, action) => {
      state.exportPopupToRender = action.payload.exportPopupToRender
    })
    builder.addCase(requestActivityLogExport.pending, () => { })
    builder.addCase(requestActivityLogExport.rejected, () => { })
    builder.addCase(requestOutgoingCommsExport.fulfilled, (state) => {
      state.exportPopupToRender = CONFIRMATION_POPUP_TYPE
    })
    builder.addCase(requestOutgoingCommsExport.pending, () => { })
    builder.addCase(requestOutgoingCommsExport.rejected, (state, action) => {
      state.exportPopupToRender = action.payload.exportPopupToRender
    })
  }
})

const resetToFirstPage = () => (dispatch) => {
  dispatch(slice.actions.setPageToken(initialState.pageToken))
  dispatch(slice.actions.setPageNumber(initialState.pageNumber))
}

export const setSearchTerm = (searchTerm) => (dispatch) => {
  const queryParams = {
    ...resetPageParams,
    search_term: searchTerm,
  }

  dispatch(resetToFirstPage())
  dispatch(slice.actions.setSearchTerm(searchTerm))
  dispatch(setGetParams(queryParams))

  dispatch(fetchActivityLogEntries())
}

export const clearFilters = () => (dispatch, getState) => {
  if (Object.keys(getState().activityLog.filters).length > 0) {
    dispatch(setGetParams(resetFiltersObj))
    dispatch(slice.actions.resetFilters())

    dispatch(fetchActivityLogEntries())
  }
}

export const setFilter = (category, filterValues) => (dispatch) => {
  const params = parseQuery(window.location.search)

  if (!params.filters) {
    params.filters = {}
  }

  params.filters[category] = filterValues

  dispatch(resetToFirstPage())
  dispatch(slice.actions.setFilter({ name: category, values: filterValues }))

  Segment.track('activity_log.applied_filter', {
    filter_type: category,
  })

  dispatch(pushQueryParams({
    ...params,
    ...resetPageParams,
  }))
  dispatch(fetchActivityLogEntries())
}

export const sort = (column) => (dispatch, getState) => {
  dispatch(slice.actions.setSorting(column))
  dispatch(slice.actions.setPageNumber(0))

  const state = getState()
  const sortField = sortFieldSelector(state)
  const sortOrder = sortOrderSelector(state)
  const queryParams = {
    page: 0,
    sort_order: sortOrder,
    sort_field: sortField,
  }

  dispatch(setGetParams(queryParams))
  dispatch(fetchActivityLogEntries())
}

export const paginate = (pageNumber, pageSize, next) => (dispatch, getState) => {
  dispatch(slice.actions.setPageNumber(pageNumber))
  dispatch(slice.actions.setPageSize(pageSize))

  const state = getState()
  const nextPageToken = activityLogPaginationSelector(state).next_page_token
  const prevPageToken = activityLogPaginationSelector(state).previous_page_token
  const sortField = sortFieldSelector(state)
  const sortOrder = sortOrderSelector(state)
  const activityLogCount = activityLogCountSelector(state)
  const searchTerm = searchTermSelector(state)

  let pageToken
  if (pageNumber !== 0) {
    pageToken = next === true ? nextPageToken : prevPageToken
  }

  const queryParams = {
    page: pageNumber,
    page_size: pageSize,
    page_token: pageToken,
    sort_field: sortField,
    sort_order: sortOrder,
    search_term: searchTerm || undefined
  }

  Segment.track('activity_log.changed_page', {
    current_page: pageNumber,
    total_pages: Math.ceil(activityLogCount.totalCount / pageSize),
  })

  dispatch(setGetParams(queryParams))
  dispatch(fetchActivityLogEntries({ next }))
}

export const init = () => (dispatch) => {
  const params = parseQuery(window.location.search)

  dispatch(slice.actions.setPageNumber(params.page ? parseInt(params.page, 10) : 0))
  dispatch(
    slice.actions.setPageSize(params.page_size ? parseInt(params.page_size, 10) : PAGE_SIZES[0])
  )

  if (params.sort_field) {
    dispatch(slice.actions.setSortField(params.sort_field))
  }

  if (params.sort_order) {
    dispatch(slice.actions.setSortOrder(params.sort_order))
  }

  if (params.page_token) {
    dispatch(slice.actions.setPageToken(params.page_token))
  }

  if (params.filters) {
    Object.entries(params.filters).forEach(([key, values]) => {
      dispatch(slice.actions.setFilter({ name: key, values }))
    })
  }

  if (params.search_term) {
    dispatch(slice.actions.setSearchTerm(params.search_term))
  }

  dispatch(fetchActivityLogEntries())
  dispatch(fetchActivityLogAggregations())
}

export const { reducer } = slice
export const { clearExportResults, reset, setShowFilterBar, setSelectedPeriod } = slice.actions
